/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { showToast } from "components/AppToast"
import ErrorModal from "components/ErrorModal"
import {
  CurrentUserParameters,
  UserParametersV2,
} from "features/userParameters/UserParametersSchemata"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"
import { parseWorkbookFile } from "services/workbooks/workbookParsingDelegation"

interface Props {
  readonly onCalculate?: () => void
  readonly onTabChange?: (tabId: string) => void
  // AIDEV-NOTE: Stays mutable because the handler passed in is resetFromUpload,
  // which feeds react-hook-form's reset(). A readonly param here would make that
  // handler unassignable under strictFunctionTypes.
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  readonly onUploadReset?: (data: CurrentUserParameters) => void
}

const XlsxUpload: FC<Readonly<Props>> = ({ onCalculate, onUploadReset }) => {
  const formContext = useFormContext<UserParametersV2>()
  const { reset } = formContext
  const [uploadError, setUploadError] = useState<string | string[] | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [_showSuccessToast, setShowSuccessToast] = useState(false)
  const [needsCalculation, setNeedsCalculation] = useState(false)
  const [_uploadedFileName, setUploadedFileName] = useState("")
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clean up toast timeout if component unmounts
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }
    }
  }, [])

  // Trigger recalculation after successful upload and form reset
  useEffect(() => {
    if (needsCalculation) {
      ;(() => {
        setNeedsCalculation(false)

        if (toastTimeoutRef.current) {
          clearTimeout(toastTimeoutRef.current)
          toastTimeoutRef.current = null
        }

        if (onCalculate) {
          onCalculate()
        }
      })()
    }
  }, [needsCalculation, onCalculate])

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (!files || files.length === 0) {
        return
      }

      setIsUploading(true)
      setUploadError(null)

      // Clear any existing toast
      setShowSuccessToast(false)
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }

      const file = files[0]
      setUploadedFileName(file.name)

      try {
        const result = await parseWorkbookFile(file)

        if (Array.isArray(result)) {
          // Result is an array of error messages
          setUploadError(result)
        } else {
          if (onUploadReset) {
            onUploadReset(result)
          } else {
            reset(result) // fallback for backward compatibility
          }
          setNeedsCalculation(true)
          setUploadError(null)

          showToast({
            type: "success",
            title: "Success",
            message: `Parameters successfully loaded from ${file.name}.`,
          })
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error parsing file"
        setUploadError(errorMessage)
      } finally {
        setIsUploading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      }
    },
    [reset, onUploadReset]
  )

  return (
    <>
      <div className="d-flex align-items-center">
        <button
          type="button"
          className="btn btn-secondary mt-3"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          Load
        </button>

        {isUploading && (
          <div className="spinner-border spinner-border-sm ms-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          className="d-none"
          id="xlsx-upload"
          accept=".xlsx"
          ref={fileInputRef}
          onChange={event => {
            void handleFileChange(event)
          }}
        />
      </div>

      <ErrorModal
        show={!!uploadError}
        title="Error Importing File"
        errorMessages={uploadError ?? ""}
        onClose={() => {
          setUploadError(null)
        }}
      />
    </>
  )
}

export default XlsxUpload
